import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import * as _ from 'lodash';
import { ConfigConstants } from '../../config/config-constants';
import { CONFIRMATION_TYPES } from '../../enums/confirmation-types.enum';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { NotificationDataInstanceToken, NotificationDataInterface } from '../../interfaces/notification-data.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { MaterialService } from '../../services/material/material.service';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';
import { ValueHelperInstanceToken, ValueHelperInterface } from '../../interfaces/value-helper.interface';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
    ODataNotificationIdentifierOutModel,
} from '../low-level/notification-identifier.model';
import { StatusModel } from '../low-level/status.model';
import { DurationModel } from '../primitive/duration.model';
import { TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import {
    ConfirmationComplaintsModelFromJson,
    ConfirmationComplaintsP58Model,
    JsonConfirmationComplaintsModel,
    ODataConfirmationComplaintsOutModel,
} from './confirmation-complaints-p58.model';
import {
    ConfirmationEquipmentModel,
    ConfirmationEquipmentModelFromJson,
    JsonConfirmationEquipmentModel,
    ODataConfirmationEquipmentOutModel,
} from './confirmation-equipment.model';
import {
    ConfirmationExpensesModel,
    ConfirmationExpensesModelFromJson,
    JsonConfirmationExpensesModel,
    ODataConfirmationExpensesOutModel,
} from './confirmation-expenses.model';
import {
    ConfirmationFollowupModel,
    ConfirmationFollowupModelFromJson,
    JsonConfirmationFollowupModel,
    ODataConfirmationFollowupOutModel,
} from './confirmation-followup.model';
import {
    ConfirmationMaterialConsumeModel,
    ODataConfirmationMaterialConsumeOutModel,
} from './confirmation-material-consume.model';
import {
    ConfirmationMeasurementToolsModel,
    ConfirmationMeasurementToolsModelFromJson,
    JsonConfirmationMeasurementToolsModel,
    ODataConfirmationMeasurementToolsOutModel,
} from './confirmation-measurement-tools.model';
import {
    ConfirmationMeasuringPointsModel,
    ConfirmationMeasuringPointsModelFromJson,
    JsonConfirmationMeasuringPointsModel,
    ODataConfirmationMeasuringPointsOutModel,
} from './confirmation-measuring-points.model';
import {
    ConfirmationParticipantsModel,
    ConfirmationParticipantsModelFromJson,
    JsonConfirmationParticipantsModel,
    ODataConfirmationParticipantsOutModel,
} from './confirmation-participants.model';
import {
    ConfirmationServiceReportModel,
    ConfirmationServiceReportModelFromJson,
    JsonConfirmationServiceReportModel,
    ODataConfirmationServiceReportOutModel,
} from './confirmation-service-report.model';
import { ConfirmationSummaryModel, ODataConfirmationSummaryOutModel } from './confirmation-summary.model';
import {
    ConfirmationTimeRecordingModel,
    ConfirmationTimeRecordingModelFromJson,
    JsonConfirmationTimeRecordingModel,
    ODataConfirmationTimeRecordingOutModel,
} from './confirmation-time-recording.model';
import {
    ConfirmationUploadToDocboxModel,
    ConfirmationUploadToDocboxModelFromJson,
    JsonConfirmationUploadToDocboxModel,
} from './confirmation-upload-to-docbox.model';
import { ODataTimeZoneOutModel } from './time-zone.model';
import {
    ConfirmationReasonMissingProtocolModel,
    JsonConfirmationReasonMissingProtocolModel,
} from './confirmation-reason-missing-protocol.model';
import {
    ConfirmationSourceReplacementsModel,
    ConfirmationSourceReplacementsModelFromJson,
    JsonConfirmationSourceReplacementsModel,
} from './confirmation-source-replacements.model';
import {
    ConfirmationInstallationPaperworkModel,
    ConfirmationInstallationPaperworkModelFromJson,
    JsonConfirmationInstallationPaperworkModel,
    ODataConfirmationInstallationPaperworkOutModel,
} from './confirmation-installation-paperwork.model';
import { TimesheetModel } from '../low-level/timesheet.model';
import { TimesheetService } from '../../services/timesheet/timesheet.service';
import { isDefined } from '../../utilities/is-defined';

/**
 * Custom merge function for use with _.mergeWith. Extends existing arrays with new entries instead of
 * overwriting the entire array when merging an array property into an object.
 *
 * @param objValue the value of the property in the object being merged into
 * @param srcValue the value of the property in the object being merged from
 * @returns the merged value or undefined if no merging should be done
 */
function mergeCustomizer(objValue: unknown, srcValue: unknown): unknown[] | undefined {
    if (Array.isArray(objValue)) {
        return objValue.concat(srcValue);
    }
}

export class ConfirmationModel extends CompositeModel {
    protected log: LogInterface;
    protected configService: ConfigInterface;
    protected notificationService: NotificationDataInterface;
    protected valueHelperService: ValueHelperInterface;
    protected materialService: MaterialService;
    protected userMasterService: UserMasterInterface;
    protected timesheetService: TimesheetService;

    public equipment: ConfirmationEquipmentModel | undefined;
    public materials: ConfirmationMaterialConsumeModel | undefined;
    public measurementTool: ConfirmationMeasurementToolsModel | undefined;
    public measuringPoints: ConfirmationMeasuringPointsModel;
    public timeRecording: ConfirmationTimeRecordingModel;
    public expenses: ConfirmationExpensesModel | undefined;
    public uploadToDocBox: ConfirmationUploadToDocboxModel | undefined;
    public participants: ConfirmationParticipantsModel;
    public sourceReplacements: ConfirmationSourceReplacementsModel | undefined;
    public serviceReport: ConfirmationServiceReportModel | undefined;
    public complaints: ConfirmationComplaintsP58Model | undefined;
    public followup: ConfirmationFollowupModel | undefined;
    public reasonMissingProtocols: ConfirmationReasonMissingProtocolModel | undefined;
    public installationPaperwork: ConfirmationInstallationPaperworkModel | undefined;
    public summary: ConfirmationSummaryModel;
    public utcDiff: DurationModel;
    public utcSign: TextModel;
    public timeZone: TextModel;
    public jobRole: TextModel;
    public shortConfirmationIdent: TextModel;
    public salesOrg: TextModel;
    public plant: TextModel;
    public submissionDateTime: TextModel;
    public notificationCurrentStatus: StatusModel;
    public isNotificationRemoteWork: boolean;
    public serviceReportLanguage: TextModel;

    // Below properties are to store the information that whether an accordion was opened or not
    // It is used for displaying appropriate status icons for respective sections
    public accordionOpenedStatus: AccordionOpenedStatus = {
        equipment: false,
        complaints: false,
        materials: false,
        measurementTool: false,
        measuringPoints: false,
        serviceReport: false,
        timeRecording: false,
        expenses: false,
        uploadToDocBox: false,
        participants: false,
        sourceReplacements: false,
        followup: false,
        installationPaperwork: false,
    };

    // This flag is used to store if the confirmation has been sent to the backend already as leaving the confirmation
    // screen after submitting is calling the ConfirmationCanDeactivateGuard, which is storing the confirmation again
    // in localStorage/IndexDB after it has been submitted and deleted from there. We use it to not store it again
    // after successful submission. It will be set in ConfirmationService.submitConfirmation only.
    // There is no need to write it out to localStorage/IndexDB in toJSON as the ConfirmationCanDeactivateGuard works
    // with the in-memory version of the confirmation from ConfirmationService.
    public isSubmitted: boolean;

    // Timestamp when the model is writing to storage (IndexedDB, Concurrent Usage Store, etc.) as UTC milliseconds
    // from Date.now() or moment().utc().valueOf() or moment().valueOf().
    public saveTimestamp: number;
    public constructor(
        public notificationIdentifier: NotificationIdentifierModel,
        public type: CONFIRMATION_TYPES,
        timesheet: TimesheetModel,
    ) {
        super('ConfirmationModel', {});

        // Dependency injection
        const injector = CustomInjector.getInjector();
        this.log = injector.get(LogInstanceToken);
        this.configService = injector.get(ConfigInstanceToken);
        this.notificationService = injector.get(NotificationDataInstanceToken);
        this.valueHelperService = injector.get(ValueHelperInstanceToken);
        this.materialService = injector.get(MaterialService);
        this.userMasterService = injector.get(UserMasterInstanceToken);
        this.timesheetService = injector.get(TimesheetService);

        const notificationModel = this.notificationService.getNotificationByIdentifier(this.notificationIdentifier);

        if (!notificationModel) {
            throw new Error(`Could not load notification ${this.notificationIdentifier} for confirmation model!`);
        }
        this.isNotificationRemoteWork = this.notificationService.isNotificationRemoteWork(notificationModel);

        this.notificationCurrentStatus = this.notificationService.getStatus(notificationModel);
        const timeRecordingBillableCodes = this.valueHelperService.getTimeRecordingBillableCodes();
        const timeRecordingTaskTypeInfoCodes = this.configService.getEnableRemoteWork()
            ? this.valueHelperService.getRemoteWorkTimeRecordingTaskTypeInfo(this.notificationCurrentStatus)
            : this.valueHelperService.getTimeRecordingTaskTypeInfo();
        const timeRecordingDestinationCodes = this.valueHelperService.getTravelDestination();
        const timeRecordingMeansOfTransportationCodes = this.valueHelperService.getMeansOfTransportation();
        this.timeRecording = new ConfirmationTimeRecordingModel(
            notificationModel,
            timeRecordingBillableCodes,
            timeRecordingTaskTypeInfoCodes,
            timeRecordingMeansOfTransportationCodes,
            timeRecordingDestinationCodes,
            timesheet,
            false,
        );
        this.accordionOpenedStatus.timeRecording = false;

        if (this.configService.showEquipment(notificationModel.taskGroup)) {
            const equipmentStatusInArrival = notificationModel.isCentriCareContract()
                ? this.valueHelperService.getCentriCareStatInArrival()
                : this.valueHelperService.getEquipmentStatusInArrival();
            const equipmentStatusAfterFix = notificationModel.isCentriCareContract()
                ? this.valueHelperService.getCentriCareStatusAfterFix()
                : this.valueHelperService.getEquipmentStatusAfterFix();
            const equipmentSalesInfo = this.valueHelperService.getEquipmentBKInfo();
            const equipmentCommercialInfoDefaultCode = this.valueHelperService.getCommercialInfoDefaultCode();

            // logic for selection value helpers is different between P58 and NGMAA
            const equipmentErrorCodes = this.valueHelperService.getEquipmentErrorCode(notificationModel.taskGroup);

            this.equipment = new ConfirmationEquipmentModel(
                notificationModel,
                this.timeRecording.timeRecordingEntries.value,
                equipmentStatusInArrival,
                equipmentStatusAfterFix,
                equipmentErrorCodes,
                equipmentSalesInfo,
                equipmentCommercialInfoDefaultCode,
            );
            this.accordionOpenedStatus.equipment = false;
        }

        if (this.configService.showMaterialConsumption(notificationModel.taskGroup)) {
            if (type === CONFIRMATION_TYPES.SHORT) {
                const pickedMaterials = this.materialService.getAllPickedMaterialsForNotification(
                    notificationModel.identifier,
                );
                this.materials =
                    pickedMaterials.length > 0 ? new ConfirmationMaterialConsumeModel(notificationModel) : undefined;
                this.accordionOpenedStatus.materials = pickedMaterials.length === 0;
            } else {
                const canUserAddMaterials =
                    this.configService.getDisplayAddMaterialButton() ||
                    notificationModel.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO;
                const localMaterials = this.materialService.getAllMaterialsForNotification(
                    notificationModel.identifier,
                );
                this.materials = new ConfirmationMaterialConsumeModel(notificationModel);
                this.accordionOpenedStatus.materials = !canUserAddMaterials && localMaterials.length === 0;
            }
        }

        if (
            this.configService.showMeasurementTools(notificationModel.taskGroup) &&
            !this.isNotificationRemoteWork &&
            (type !== CONFIRMATION_TYPES.SHORT || this.configService.getEnableMeasurementToolsForSCRM())
        ) {
            this.measurementTool = new ConfirmationMeasurementToolsModel(
                notificationModel.notificationType,
                notificationModel.processIndicator,
                notificationModel.isApplicationSpecialist,
                this.equipment ? this.equipment.equipments.selectedEntry.model : undefined,
                [],
                notificationModel,
            );
            this.accordionOpenedStatus.measurementTool = false;
        }

        const isMeasuringPointsAvailable = notificationModel.equipmentSet.some(
            (equipment) => equipment.measuringPoint.value,
        );
        if (
            isMeasuringPointsAvailable &&
            this.configService.showMeasuringPoints(notificationModel.taskGroup) &&
            type !== CONFIRMATION_TYPES.SHORT
        ) {
            this.measuringPoints = new ConfirmationMeasuringPointsModel(notificationModel.equipmentSet);
            this.accordionOpenedStatus.measuringPoints = false;
        }

        const displayDistance = this.configService.getDisplayConfirmationDistance();
        const displayExpenses =
            !this.userMasterService.isPaceOnlineUser() &&
            this.timesheetService.isTimesheetFeatureEnabled() &&
            this.configService.getDisplayConfirmationExpenses();
        const displayAdditionalTravelCost = this.configService.getDisplayAdditionalTravelCost();

        if ((displayDistance || displayExpenses || displayAdditionalTravelCost) && !this.isNotificationRemoteWork) {
            this.expenses = new ConfirmationExpensesModel(
                notificationIdentifier,
                this.isNotificationRemoteWork,
                '',
                false,
            );
            this.accordionOpenedStatus.expenses = false;
        }

        if (type !== CONFIRMATION_TYPES.SHORT) {
            this.serviceReport = new ConfirmationServiceReportModel(notificationModel);
            this.accordionOpenedStatus.serviceReport = false;
        }

        const isLongOrRequired =
            type !== CONFIRMATION_TYPES.SHORT &&
            this.configService.getShowUploadToDocBoxForNotificationType(notificationModel.notificationType) &&
            this.valueHelperService.getUploadToDocboxValueHelpers().length > 0 &&
            !this.userMasterService.isPaceOnlineUser();

        const isShortMultiDay =
            type === CONFIRMATION_TYPES.SHORT && this.notificationService.isMultiDayNotification(notificationModel);

        if (isLongOrRequired || isShortMultiDay) {
            this.uploadToDocBox = new ConfirmationUploadToDocboxModel([], notificationModel);
            this.accordionOpenedStatus.uploadToDocBox = false;
        }

        if (this.configService.showParticipantList(notificationModel.taskGroup) && type !== CONFIRMATION_TYPES.SHORT) {
            this.participants = new ConfirmationParticipantsModel(_.cloneDeep(notificationModel.participantSet));
            this.accordionOpenedStatus.participants = false;
        }

        if (this.configService.isSourceReplacementEnabled() && notificationModel.sourceReplacements?.length > 0) {
            this.sourceReplacements = new ConfirmationSourceReplacementsModel(
                notificationIdentifier,
                notificationModel.sourceReplacements,
            );
            this.accordionOpenedStatus.sourceReplacements = false;
        }

        if (this.configService.getComplaintNotificationType(notificationModel.notificationType)) {
            this.complaints = new ConfirmationComplaintsP58Model(notificationIdentifier);
            this.accordionOpenedStatus.complaints = false;
        }

        if (this.configService.showFollowup(notificationModel.taskGroup)) {
            this.followup = new ConfirmationFollowupModel(
                notificationModel,
                notificationModel.notificationType,
                type,
                this.materialService.isMaterialRequestedForcesFollowup(),
                this.equipment ? this.equipment.getEquipmentStatus() : undefined,
                this.materials ? this.materials.listMaterial.value : undefined,
            );
            this.accordionOpenedStatus.followup = false;
        }

        this.utcDiff = <DurationModel>this.set(notificationModel.timeZone.utcDiff);

        this.utcSign = <TextModel>this.set(notificationModel.timeZone.utcSign);

        this.timeZone = <TextModel>this.set(notificationModel.timeZone.timeZone);

        this.jobRole = <TextModel>this.set(new TextModel('JobRole', notificationModel.taskGroup));

        this.shortConfirmationIdent = <TextModel>(
            this.set(new TextModel('ShortConfirmationIdent', type === CONFIRMATION_TYPES.SHORT ? 'X' : ''))
        );

        this.serviceReportLanguage = <TextModel>this.set(new TextModel('Language', notificationModel.languageId));

        if (notificationModel.isPaceFormsEnabled && this.configService.getDocMissingProtocolsEnabled()) {
            this.reasonMissingProtocols = new ConfirmationReasonMissingProtocolModel(
                notificationModel.notificationType,
            );
        }

        if (this.notificationService.displayInstallationPaperwork(this.notificationIdentifier)) {
            this.installationPaperwork = new ConfirmationInstallationPaperworkModel(notificationModel);
        }

        this.isSubmitted = false;
        this.saveTimestamp = 0;
    }

    public toJSON(): Partial<ConfirmationModel> {
        return {
            complaints: this.complaints,
            equipment: this.equipment,
            expenses: this.expenses,
            followup: this.followup,
            measurementTool: this.measurementTool,
            measuringPoints: this.measuringPoints,
            notificationIdentifier: this.notificationIdentifier,
            uploadToDocBox: this.uploadToDocBox,
            participants: this.participants,
            sourceReplacements: this.sourceReplacements,
            serviceReport: this.serviceReport,
            timeRecording: this.timeRecording,
            installationPaperwork: this.installationPaperwork,
            // Serialized so the NgRx slice keeps the documented reason: its submit rebuilds the model
            // from the stored confirmation, where the legacy one submitted this very instance.
            reasonMissingProtocols: this.reasonMissingProtocols,
            accordionOpenedStatus: {
                equipment: this.accordionOpenedStatus.equipment,
                complaints: this.accordionOpenedStatus.complaints,
                materials: this.accordionOpenedStatus.materials,
                measurementTool: this.accordionOpenedStatus.measurementTool,
                measuringPoints: this.accordionOpenedStatus.measuringPoints,
                serviceReport: this.accordionOpenedStatus.serviceReport,
                timeRecording: this.accordionOpenedStatus.timeRecording,
                expenses: this.accordionOpenedStatus.expenses,
                uploadToDocBox: this.accordionOpenedStatus.uploadToDocBox,
                participants: this.accordionOpenedStatus.participants,
                sourceReplacements: this.accordionOpenedStatus.sourceReplacements,
                followup: this.accordionOpenedStatus.followup,
                installationPaperwork: this.accordionOpenedStatus.installationPaperwork,
            },
            saveTimestamp: this.saveTimestamp,
        };
    }

    public getOutModel(): ODataConfirmationOutModel {
        if (isDefined(this.installationPaperwork)) {
            this.followup?.setFollowUpForInstallationPaperwork(this.installationPaperwork.generateFollowUpShortText());
        }
        const outModel = <ODataConfirmationOutModel>super.getOutModel();

        // we need to use lodash mergeWith here because we want to not overwrite any sets
        // (eg. CrmCodesSet (equipment and time recording are adding values to this set))
        _.mergeWith(outModel, this.notificationIdentifier.getOutModel());
        _.mergeWith(outModel, this.equipment ? this.equipment.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.materials ? this.materials.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.measurementTool ? this.measurementTool.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.serviceReport ? this.serviceReport.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.measuringPoints ? this.measuringPoints.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.participants ? this.participants.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.sourceReplacements ? this.sourceReplacements.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.uploadToDocBox ? this.uploadToDocBox.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.followup ? this.followup.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.expenses ? this.expenses.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.complaints ? this.complaints.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(outModel, this.timeRecording ? this.timeRecording.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(
            outModel,
            this.installationPaperwork ? this.installationPaperwork.getOutModel() : {},
            mergeCustomizer,
        );
        // no summary for PACE US
        _.mergeWith(outModel, this.summary ? this.summary.getOutModel() : {}, mergeCustomizer);
        _.mergeWith(
            outModel,
            this.reasonMissingProtocols ? this.reasonMissingProtocols.getOutModel() : {},
            mergeCustomizer,
        );

        return outModel;
    }
}

export class ConfirmationModelFromJson extends ConfirmationModel {
    public constructor(data: JsonConfirmationModel, type: CONFIRMATION_TYPES, timesheet: TimesheetModel) {
        super(new NotificationIdentifierModelFromJson(data.notificationIdentifier), type, timesheet);
        const notificationModel = this.notificationService.getNotificationByIdentifier(
            new NotificationIdentifierModelFromJson(data.notificationIdentifier),
        );

        if (!notificationModel) {
            throw new Error(
                `Could not load notification ${this.notificationIdentifier} for confirmation model from json!`,
            );
        }

        const equipmentStatusInArrival = notificationModel.isCentriCareContract()
            ? this.valueHelperService.getCentriCareStatInArrival()
            : this.valueHelperService.getEquipmentStatusInArrival();
        const equipmentStatusAfterFix = notificationModel.isCentriCareContract()
            ? this.valueHelperService.getCentriCareStatusAfterFix()
            : this.valueHelperService.getEquipmentStatusAfterFix();
        const equipmentSalesInfo = this.valueHelperService.getEquipmentBKInfo();
        const equipmentCommercialInfoDefaultCode = this.valueHelperService.getCommercialInfoDefaultCode();

        // logic for selection value helpers is different between P58 and NGMAA
        const equipmentErrorCodes = this.valueHelperService.getEquipmentErrorCode(notificationModel.taskGroup);

        const timeRecordingBillableCodes = this.valueHelperService.getTimeRecordingBillableCodes();
        // `updatedStatus` is unset until the status is changed on the device, so read the effective status the same
        // way the base constructor does - otherwise restoring a confirmation throws and the caller silently falls
        // back to a default model, resetting the time recording entries
        const statusModel = this.notificationService.getStatus(notificationModel);
        const timeRecordingTaskTypeInfoCodes = this.configService.getEnableRemoteWork()
            ? this.valueHelperService.getRemoteWorkTimeRecordingTaskTypeInfo(statusModel)
            : this.valueHelperService.getTimeRecordingTaskTypeInfo();

        const timeRecordingDestinationCodes = this.valueHelperService.getTravelDestination();
        const timeRecordingMeansOfTransportationCodes = this.valueHelperService.getMeansOfTransportation();

        const timeRecording = data.timeRecording
            ? new ConfirmationTimeRecordingModelFromJson(
                  data.timeRecording,
                  notificationModel,
                  timeRecordingBillableCodes,
                  timeRecordingTaskTypeInfoCodes,
                  timeRecordingMeansOfTransportationCodes,
                  timeRecordingDestinationCodes,
                  timesheet,
                  false,
              )
            : undefined;
        if (timeRecording) {
            this.timeRecording = timeRecording;
            this.accordionOpenedStatus.timeRecording = data.accordionOpenedStatus?.timeRecording ?? false;
        }

        const equipment = data.equipment
            ? new ConfirmationEquipmentModelFromJson(
                  data.equipment,
                  notificationModel,
                  timeRecording ? this.timeRecording.timeRecordingEntries.value : [],
                  equipmentStatusInArrival,
                  equipmentStatusAfterFix,
                  equipmentErrorCodes,
                  equipmentSalesInfo,
                  equipmentCommercialInfoDefaultCode,
              )
            : undefined;
        if (equipment) {
            this.equipment = equipment;
            this.accordionOpenedStatus.equipment = data.accordionOpenedStatus?.equipment ?? false;
        }

        if (this.notificationService.displayInstallationPaperwork(this.notificationIdentifier)) {
            this.installationPaperwork = data.installationPaperwork
                ? new ConfirmationInstallationPaperworkModelFromJson(data.installationPaperwork, notificationModel)
                : undefined;
            this.accordionOpenedStatus.installationPaperwork =
                data.accordionOpenedStatus?.installationPaperwork ?? false;
        }

        if (this.configService.showMaterialConsumption(notificationModel.taskGroup)) {
            // special case for material as we do not load confirmation back from file service
            // so always a material model use to be created without checking condition if material has to be shown or no
            const pickedMaterials = this.materialService.getAllPickedMaterialsForNotification(
                notificationModel.identifier,
            );
            if (
                type !== CONFIRMATION_TYPES.SHORT ||
                (type === CONFIRMATION_TYPES.SHORT && pickedMaterials.length > 0)
            ) {
                this.materials = new ConfirmationMaterialConsumeModel(notificationModel);
                this.accordionOpenedStatus.materials = data.accordionOpenedStatus?.materials ?? false;
            }
        }
        if (
            data.measurementTool &&
            !this.isNotificationRemoteWork &&
            (type !== CONFIRMATION_TYPES.SHORT || this.configService.getEnableMeasurementToolsForSCRM())
        ) {
            this.measurementTool = new ConfirmationMeasurementToolsModelFromJson(
                notificationModel.notificationType,
                notificationModel.processIndicator,
                notificationModel.isApplicationSpecialist,
                this.equipment ? this.equipment.equipments.selectedEntry.model : undefined,
                data.measurementTool,
                notificationModel,
            );
            this.accordionOpenedStatus.measurementTool = data.accordionOpenedStatus?.measurementTool ?? false;
        }
        // this check is necessary to hide the sections in the short confirmation when going
        // from a saved full confirmation to a short confirmation
        if (type !== CONFIRMATION_TYPES.SHORT) {
            const measuringPoints = data.measuringPoints
                ? new ConfirmationMeasuringPointsModelFromJson(data.measuringPoints, notificationModel.equipmentSet)
                : undefined;
            // Migration logic starts
            // it is possible that measuringPoints model is saved in local storage even when there are no
            // measuring points to be displayed, hence it is checked again
            const isMeasuringPointsAvailable = notificationModel.equipmentSet.some((e) => e.measuringPoint.value);
            // Migration logic ends
            if (measuringPoints && isMeasuringPointsAvailable) {
                this.measuringPoints = measuringPoints;
                this.accordionOpenedStatus.measuringPoints = data.accordionOpenedStatus?.measuringPoints ?? false;
            }

            const serviceReport = data.serviceReport
                ? new ConfirmationServiceReportModelFromJson(data.serviceReport, notificationModel)
                : undefined;
            if (serviceReport) {
                this.serviceReport = serviceReport;
                this.accordionOpenedStatus.serviceReport = data.accordionOpenedStatus?.serviceReport ?? false;
            }

            const uploadToDocBox = data.uploadToDocBox
                ? new ConfirmationUploadToDocboxModelFromJson(data.uploadToDocBox, notificationModel)
                : undefined;
            if (uploadToDocBox) {
                this.uploadToDocBox = uploadToDocBox;
                this.accordionOpenedStatus.uploadToDocBox = data.accordionOpenedStatus?.uploadToDocBox ?? false;
            }

            const participants = data.participants
                ? new ConfirmationParticipantsModelFromJson(data.participants)
                : undefined;
            if (participants) {
                this.participants = participants;
                this.accordionOpenedStatus.participants = data.accordionOpenedStatus?.participants ?? false;
            }

            const sourceReplacements = data.sourceReplacements
                ? new ConfirmationSourceReplacementsModelFromJson(data.sourceReplacements, notificationModel.identifier)
                : undefined;
            if (sourceReplacements) {
                this.sourceReplacements = sourceReplacements;
                this.accordionOpenedStatus.sourceReplacements = data.accordionOpenedStatus?.sourceReplacements ?? false;
            }
        }

        const followup = data.followup
            ? new ConfirmationFollowupModelFromJson(
                  data.followup,
                  notificationModel,
                  notificationModel.notificationType,
                  type,
                  this.materialService.isMaterialRequestedForcesFollowup(),
                  equipment ? equipment.getEquipmentStatus() : undefined,
                  this.materials ? this.materials.listMaterial.value : undefined,
                  this.configService.getDisplayTrendCodeInFollowup() && data.equipment ? data.equipment : undefined, // migration logic SHP-3884
              )
            : undefined;
        if (followup) {
            this.followup = followup;
            this.accordionOpenedStatus.followup = data.accordionOpenedStatus?.followup ?? false;
        }

        // expenses get created only if some specific conditions/config flag occurs. If the super is not having any expenses, then we don't need to re-create them here at all
        if (this.expenses) {
            this.expenses = data.expenses
                ? new ConfirmationExpensesModelFromJson(data.expenses, this.isNotificationRemoteWork)
                : undefined;
            this.accordionOpenedStatus.expenses = data.accordionOpenedStatus?.expenses ?? false;
        }

        const complaints = data.complaints
            ? new ConfirmationComplaintsModelFromJson(data.complaints, notificationModel.identifier)
            : undefined;
        if (complaints) {
            this.complaints = complaints;
            this.accordionOpenedStatus.complaints = data.accordionOpenedStatus?.complaints ?? false;
        }

        this.saveTimestamp = data.saveTimestamp ?? 0;
    }
}

export interface JsonConfirmationModel {
    notificationIdentifier: JsonNotificationIdentifierModel;
    equipment: JsonConfirmationEquipmentModel;
    measurementTool: JsonConfirmationMeasurementToolsModel;
    measuringPoints: JsonConfirmationMeasuringPointsModel;
    serviceReport: JsonConfirmationServiceReportModel;
    uploadToDocBox: JsonConfirmationUploadToDocboxModel;
    participants: JsonConfirmationParticipantsModel;
    sourceReplacements: JsonConfirmationSourceReplacementsModel;
    expenses: JsonConfirmationExpensesModel;
    complaints: JsonConfirmationComplaintsModel;
    followup: JsonConfirmationFollowupModel;
    timeRecording: JsonConfirmationTimeRecordingModel;
    installationPaperwork: JsonConfirmationInstallationPaperworkModel;
    // Optional: absent until a reason is documented, and in everything stored before it was serialized.
    reasonMissingProtocols?: JsonConfirmationReasonMissingProtocolModel;
    type: CONFIRMATION_TYPES;
    accordionOpenedStatus: {
        equipment: boolean;
        complaints: boolean;
        complaintsAA: boolean;
        materials: boolean;
        measurementTool: boolean;
        measuringPoints: boolean;
        serviceReport: boolean;
        timeRecording: boolean;
        expenses: boolean;
        uploadToDocBox: boolean;
        participants: boolean;
        sourceReplacements: boolean;
        followup: boolean;
        installationPaperwork: boolean;
    };
    saveTimestamp: number;
}

export interface ODataConfirmationOutModel
    extends
        ODataNotificationIdentifierOutModel,
        ODataConfirmationEquipmentOutModel,
        ODataConfirmationFollowupOutModel,
        ODataConfirmationSummaryOutModel,
        ODataConfirmationExpensesOutModel,
        ODataConfirmationTimeRecordingOutModel,
        ODataConfirmationMaterialConsumeOutModel,
        ODataConfirmationComplaintsOutModel,
        ODataConfirmationMeasurementToolsOutModel,
        ODataConfirmationMeasuringPointsOutModel,
        ODataConfirmationParticipantsOutModel,
        ODataConfirmationServiceReportOutModel,
        ODataConfirmationInstallationPaperworkOutModel,
        ODataTimeZoneOutModel,
        ODataObject {
    JobRole: string;
    ShortConfirmationIdent: string;
    SalesOrg: string;
    Plant: string;
}

interface AccordionOpenedStatus {
    [key: string]: boolean; // Index signature
    equipment: boolean;
    complaints: boolean;
    materials: boolean;
    measurementTool: boolean;
    measuringPoints: boolean;
    serviceReport: boolean;
    timeRecording: boolean;
    expenses: boolean;
    uploadToDocBox: boolean;
    participants: boolean;
    sourceReplacements: boolean;
    followup: boolean;
    installationPaperwork: boolean;
}
